/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import {
  type IntelligenceData,
  IntelligenceType,
} from '@coze-arch/idl/intelligence_api';
import { I18n } from '@coze-arch/i18n';
import { Avatar } from '@coze-arch/coze-design';

import { filterQuickOpenItems } from '../../hooks/use-quick-open';

import styles from './index.module.less';

export interface QuickOpenPaletteProps {
  open: boolean;
  shortcutLabel: string;
  items: IntelligenceData[];
  onClose: () => void;
  onSelect: (item: IntelligenceData) => void;
}

interface QuickOpenItemProps {
  item: IntelligenceData;
  active: boolean;
  onHover: () => void;
  onSelect: () => void;
}

const QuickOpenItem: FC<QuickOpenItemProps> = ({
  item,
  active,
  onHover,
  onSelect,
}) => {
  const { name, description, icon_url } = item.basic_info ?? {};
  const isAgent = item.type === IntelligenceType.Bot;
  const isPublished = item.publish_info?.has_published;

  return (
    <button
      type="button"
      className={classNames(styles.item, active && styles.active)}
      onMouseEnter={onHover}
      onClick={onSelect}
      data-testid="abr-quick-open-item"
    >
      <Avatar className={styles.avatar} size="small" src={icon_url} />
      <div className={styles.meta}>
        <div className={styles.name}>{name}</div>
        {description ? <div className={styles.desc}>{description}</div> : null}
      </div>
      <div className={styles.badges}>
        <span className={styles.typeBadge}>
          {isAgent
            ? I18n.t('abr_home_type_agent')
            : I18n.t('abr_home_type_workflow')}
        </span>
        <span className={isPublished ? styles.statusLive : styles.statusDraft}>
          {isPublished ? 'LIVE' : 'DRAFT'}
        </span>
      </div>
    </button>
  );
};

function useQuickOpenKeyboard({
  open,
  filteredItems,
  activeIndex,
  setActiveIndex,
  onClose,
  onSelect,
}: {
  open: boolean;
  filteredItems: IntelligenceData[];
  activeIndex: number;
  setActiveIndex: (updater: (prev: number) => number) => void;
  onClose: () => void;
  onSelect: (item: IntelligenceData) => void;
}): void {
  useEffect(() => {
    if (!open) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (!filteredItems.length) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex(prev => (prev + 1) % filteredItems.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(
          prev => (prev - 1 + filteredItems.length) % filteredItems.length,
        );
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const target = filteredItems[activeIndex];
        if (target) {
          onSelect(target);
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeIndex, filteredItems, onClose, onSelect, open, setActiveIndex]);
}

export const QuickOpenPalette: FC<QuickOpenPaletteProps> = ({
  open,
  shortcutLabel,
  items,
  onClose,
  onSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredItems = useMemo(
    () => filterQuickOpenItems(items, query),
    [items, query],
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
      return;
    }

    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useQuickOpenKeyboard({
    open,
    filteredItems,
    activeIndex,
    setActiveIndex,
    onClose,
    onSelect,
  });

  if (!open) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="presentation"
      data-testid="abr-quick-open-overlay"
    >
      <div
        className={styles.panel}
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={I18n.t('abr_home_quick_open')}
      >
        <div className={styles.searchRow}>
          <span className={styles.searchIcon} aria-hidden>
            🔍
          </span>
          <input
            ref={inputRef}
            className={styles.searchInput}
            value={query}
            placeholder={I18n.t('abr_home_quick_open_placeholder')}
            onChange={event => setQuery(event.target.value)}
            data-testid="abr-quick-open-input"
          />
          <kbd className={styles.shortcut}>{shortcutLabel}</kbd>
        </div>

        {filteredItems.length ? (
          <div className={styles.list}>
            {filteredItems.map((item, index) => (
              <QuickOpenItem
                key={item.basic_info?.id ?? index}
                item={item}
                active={index === activeIndex}
                onHover={() => setActiveIndex(index)}
                onSelect={() => onSelect(item)}
              />
            ))}
          </div>
        ) : (
          <div className={styles.empty}>
            {I18n.t('abr_home_quick_open_empty')}
          </div>
        )}

        <div className={styles.footer}>
          {I18n.t('abr_home_quick_open_hint')}
        </div>
      </div>
    </div>
  );
};
